import DownloadIcon from '@mui/icons-material/Download';
import {
  Alert,
  Checkbox,
  Grid,
  LinearProgress,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import { DialogChromeFooter, DialogChromeHeader } from 'components/atoms/Dialog/DialogChrome';
import { useConfirmation } from 'components/modals/ConfirmationContext';
import { getPermanentMacStyleScrollbarStyles } from 'components/theme/theme';
import { first, forEach, identity, includes, isEmpty, isEqual, last, map, size, split, times } from 'es-toolkit/compat';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { downloadFileUrlWithFileName } from 'utils/exports';
import usePrevious from 'utils/usePrevious';
import { useRangeSelection } from 'utils/useRangeSelection';
import {
  assessZipSelection,
  downloadFilesAsZip,
  FileToZip,
  ZipDownloadProgress,
  ZipSelectionAssessment,
} from 'utils/zipDownload';

export interface FileToDownload {
  id?: string;
  url: string;
  fileName?: string;
  title?: string;
  subTitle?: string;
  contentLength?: number;
  caseId?: string;
  slideId?: string;
  orchestrationId?: string;
  // Produces the bytes to archive instead of fetching `url`; a rejection fails the whole archive.
  prepare?: (signal: AbortSignal) => Promise<Blob>;
}

export interface ZipDownloadConfiguration {
  archiveName: string;
  refreshFiles?: (selectedFiles: FileToDownload[], signal: AbortSignal) => Promise<FileToDownload[]>;
  download?: typeof downloadFilesAsZip;
  assessSelection?: typeof assessZipSelection;
}

export const fileNameFromPresignedUrlRegex = /\/([^/]*)?\?/;
export const fileNameFromPresignedUrlQueryStringRegex = /attachment%3B%20filename%3D([^&]+)/;

export const getLastPartOfUrlPath = (url: string): string | null => {
  // If no regex matches, try to extract the last part of the path, excluding query parameters
  const urlObj = new URL(url);
  const pathname = urlObj.pathname;
  const segments = split(pathname, '/');
  return !isEmpty(segments) ? last(segments) : null;
};

export const getFileNameFromUrl = (url: string): string =>
  decodeURIComponent(
    url.match(fileNameFromPresignedUrlQueryStringRegex)?.[1] ||
      url.match(fileNameFromPresignedUrlRegex)?.[1] ||
      getLastPartOfUrlPath(url) ||
      ''
  );

const downloadFile = (file: FileToDownload) => {
  if (file) {
    if (file.fileName) {
      downloadFileUrlWithFileName(file.url, file.fileName);
    } else {
      window.open(file.url);
    }
  }
};

const SingleItemDownload: React.FC<{
  file: FileToDownload;
  checkbox?: React.ReactNode;
  index?: number;
  disabled?: boolean;
  setDidDownload: React.Dispatch<React.SetStateAction<boolean>>;
}> = ({ file, checkbox, index, disabled, setDidDownload }) => {
  const fileTitle = file.title ? file.title : getFileNameFromUrl(file.url) || file.url;

  return (
    <MenuItem
      to={file.url}
      download={file.fileName}
      target="_blank"
      rel="noopener noreferrer"
      component={RouterLink}
      disabled={disabled}
      onClick={() => !disabled && setDidDownload(true)}
    >
      {checkbox ? <ListItemIcon>{checkbox}</ListItemIcon> : null}
      {index !== undefined ? (
        <ListItemIcon>
          <Typography>{index + 1})</Typography>
        </ListItemIcon>
      ) : null}
      <ListItemText primary={fileTitle} secondary={file.subTitle} />
      <ListItemIcon>
        <DownloadIcon />
      </ListItemIcon>
    </MenuItem>
  );
};

const formatSelectionSize = (bytes: number) => {
  const mebibytes = bytes / (1024 * 1024);
  return mebibytes >= 1 ? `${mebibytes.toFixed(1)} MiB` : `${Math.ceil(bytes / 1024)} KiB`;
};

export const DownloadFilesModal: React.FC<{
  open: boolean;
  onClose: () => void;
  filesToDownload: FileToDownload[];
  unsortedFileNames?: boolean;
  dialogTitle?: React.ReactNode;
  emptyMessage?: React.ReactNode;
  zipConfiguration?: ZipDownloadConfiguration;
}> = ({
  open,
  onClose,
  filesToDownload,
  unsortedFileNames = false,
  dialogTitle = 'Download Results',
  emptyMessage = 'No files available for download.',
  zipConfiguration,
}) => {
  const theme = useTheme();

  const allFileIndices = React.useMemo(() => times(size(filesToDownload), identity), [size(filesToDownload)]);
  const [selectedFileIndicesState, setSelectedFileIndices] = React.useState<number[] | undefined>(undefined);
  const selectedFileIndices = selectedFileIndicesState || allFileIndices;
  const allFilesSelected = size(selectedFileIndices) === size(filesToDownload);

  const [didDownload, setDidDownload] = React.useState(false);
  const [zipState, setZipState] = React.useState<
    'idle' | 'fetching' | 'archiving' | 'saving' | 'succeeded' | 'failed' | 'cancelled'
  >('idle');
  const [zipProgress, setZipProgress] = React.useState<ZipDownloadProgress>();
  const [zipError, setZipError] = React.useState<string>();
  const [selectionAssessment, setSelectionAssessment] = React.useState<ZipSelectionAssessment>();
  const zipAbortController = React.useRef<AbortController>();
  const { clearSelectionPreview, previewedSelectedItems, previewSelection, resetRangeSelection, toggleSelection } =
    useRangeSelection({
      orderedItems: allFileIndices,
      selectedItems: selectedFileIndices,
      setSelectedItems: setSelectedFileIndices,
    });
  const displayedSelectedFileIndices = previewedSelectedItems || selectedFileIndices;
  const zipEnabled = Boolean(zipConfiguration) && size(filesToDownload) > 1;
  const zipActive = includes(['fetching', 'archiving', 'saving'], zipState);

  const previousFilesToDownload = usePrevious(filesToDownload);
  React.useEffect(() => {
    const shouldResetSelection = !isEqual(filesToDownload, previousFilesToDownload) || !open;
    if (shouldResetSelection) resetRangeSelection();
    if (shouldResetSelection) {
      setSelectedFileIndices(undefined); // Reset selection when modal is closed or files change
      setDidDownload(false);
      setZipState('idle');
      setZipProgress(undefined);
      setZipError(undefined);
    }
  }, [open, filesToDownload]);

  const confirmWithModal = useConfirmation();

  const singleFile = size(filesToDownload) === 1 ? first(filesToDownload) : null;

  const selectedFiles = React.useMemo(
    () => map(selectedFileIndices, (index) => filesToDownload[index]),
    [filesToDownload, selectedFileIndices]
  );
  const assessSelection = zipConfiguration?.assessSelection;

  React.useEffect(() => {
    let current = true;
    if (!zipEnabled || isEmpty(selectedFiles)) {
      setSelectionAssessment(undefined);
      return () => {
        current = false;
      };
    }

    (assessSelection || assessZipSelection)(selectedFiles).then((assessment) => {
      if (current) setSelectionAssessment(assessment);
    });
    return () => {
      current = false;
    };
  }, [assessSelection, selectedFiles, zipEnabled]);

  const startZipDownload = async () => {
    const config = zipConfiguration;
    if (!config) return;

    const controller = new AbortController();
    zipAbortController.current = controller;
    setZipError(undefined);
    setZipState('fetching');

    try {
      const filesForDownload = config.refreshFiles
        ? await config.refreshFiles(selectedFiles, controller.signal)
        : selectedFiles;
      const assessment = await (config.assessSelection || assessZipSelection)(filesForDownload);
      setSelectionAssessment(assessment);
      if (assessment.blockingError) {
        setZipError(assessment.blockingError);
        setZipState('failed');
        return;
      }

      await (config.download || downloadFilesAsZip)({
        files: filesForDownload as FileToZip[],
        archiveName: config.archiveName,
        signal: controller.signal,
        onProgress: (progress) => {
          setZipProgress(progress);
          setZipState(progress.phase);
        },
      });
      setDidDownload(true);
      setZipState('succeeded');
    } catch (error) {
      if (controller.signal.aborted) {
        setZipState('cancelled');
      } else {
        setZipError(error instanceof Error ? error.message : 'The ZIP archive could not be created.');
        setZipState('failed');
      }
    } finally {
      if (zipAbortController.current === controller) zipAbortController.current = undefined;
    }
  };

  const handleClose = async (event: React.SyntheticEvent) => {
    event.stopPropagation();
    if (zipActive) {
      const shouldCancel = await confirmWithModal({
        title: 'Cancel ZIP download?',
        text: 'The archive is still being prepared. Cancel it and close this window?',
      });
      if (!shouldCancel) return;
      zipAbortController.current?.abort(new DOMException('Cancelled', 'AbortError'));
      setZipState('cancelled');
      onClose();
      return;
    }
    if (
      isEmpty(filesToDownload) ||
      didDownload ||
      (await confirmWithModal(
        singleFile
          ? {
              title: 'The file was not downloaded',
              text: 'Are you sure you want to continue without downloading the file?',
            }
          : {
              title: 'No files were downloaded',
              text: 'Are you sure you want to continue without downloading any files?',
            }
      ))
    ) {
      onClose();
      setDidDownload(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xl">
      <DialogChromeHeader title={dialogTitle} onClose={handleClose} sticky />
      {!isEmpty(filesToDownload) ? (
        <DialogContent dividers>
          <Grid container direction="column" spacing={1}>
            {singleFile ? (
              // Streamlined UI for single file download
              <>
                <Grid item>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    Your file is ready to download:
                  </Typography>
                </Grid>
                <Grid item>
                  <SingleItemDownload file={singleFile} setDidDownload={setDidDownload} />
                </Grid>
              </>
            ) : (
              <>
                <Grid item>
                  <Typography variant="body1">
                    Select the files you would like to download. Click the button below to start downloading.
                  </Typography>
                </Grid>
                <Grid item>
                  <Button
                    variant="contained"
                    onClick={(event) => {
                      event.stopPropagation();
                      if (zipEnabled) {
                        startZipDownload();
                        return;
                      }
                      forEach(selectedFileIndices, (index) => {
                        const file = filesToDownload[index];
                        return downloadFile(file);
                      });
                      setDidDownload(true);
                    }}
                    startIcon={<DownloadIcon />}
                    disabled={isEmpty(selectedFileIndices) || zipActive || Boolean(selectionAssessment?.blockingError)}
                  >
                    {zipEnabled
                      ? `${includes(['failed', 'cancelled'], zipState) ? 'Retry' : 'Download'} Selected as ZIP (${size(
                          selectedFileIndices
                        )} files)`
                      : `Download ${allFilesSelected ? 'All' : 'Selected'} Files (${size(selectedFileIndices)} files)`}
                  </Button>
                </Grid>
                {zipEnabled && selectionAssessment?.warning ? (
                  <Grid item>
                    <Alert severity="warning">{selectionAssessment.warning}</Alert>
                  </Grid>
                ) : null}
                {zipEnabled && selectionAssessment?.blockingError ? (
                  <Grid item>
                    <Alert severity="error">{selectionAssessment.blockingError}</Alert>
                  </Grid>
                ) : null}
                {zipEnabled && selectionAssessment ? (
                  <Grid item>
                    <Typography variant="body2" color="text.secondary">
                      {selectionAssessment.knownTotalBytes === undefined
                        ? 'Total size is unavailable. Retrieval progress will use completed files.'
                        : `Selected size: ${formatSelectionSize(selectionAssessment.knownTotalBytes)}`}
                    </Typography>
                  </Grid>
                ) : null}
                {zipEnabled && zipError ? (
                  <Grid item>
                    <Alert severity="error">{zipError}</Alert>
                  </Grid>
                ) : null}
                {zipEnabled && zipProgress && zipActive ? (
                  <Grid item>
                    <Stack spacing={1} role="status" aria-live="polite">
                      <Typography variant="body2">
                        {zipState === 'fetching'
                          ? `Retrieving files (${zipProgress.completedFiles} of ${zipProgress.totalFiles})`
                          : zipState === 'archiving'
                          ? 'Building ZIP archive'
                          : 'Saving ZIP archive'}
                      </Typography>
                      <LinearProgress
                        variant={
                          zipProgress.totalBytes || zipProgress.archiveProgress !== undefined
                            ? 'determinate'
                            : 'indeterminate'
                        }
                        value={
                          zipState === 'archiving'
                            ? (zipProgress.archiveProgress || 0) * 100
                            : zipProgress.totalBytes
                            ? (zipProgress.loadedBytes / zipProgress.totalBytes) * 100
                            : undefined
                        }
                      />
                    </Stack>
                  </Grid>
                ) : null}
                {zipEnabled && zipState === 'succeeded' ? (
                  <Grid item>
                    <Alert severity="success">The ZIP archive was saved.</Alert>
                  </Grid>
                ) : null}
                <Grid item>
                  <Button
                    color="secondary"
                    disabled={zipActive}
                    onClick={(e) => {
                      e.stopPropagation();
                      resetRangeSelection();
                      setSelectedFileIndices(!allFilesSelected ? allFileIndices : []); // Select all or deselect all
                    }}
                  >
                    {!allFilesSelected ? 'Select All' : 'Deselect All'}
                  </Button>
                </Grid>
                <Grid item>
                  <MenuList
                    sx={{
                      overflowY: 'auto',
                      maxHeight: '60vh',
                      ...getPermanentMacStyleScrollbarStyles(theme),
                    }}
                  >
                    {map(filesToDownload, (file, index) => {
                      const fileIsSelected = includes(selectedFileIndices, index);
                      const fileIsDisplayedAsSelected = includes(displayedSelectedFileIndices, index);
                      const fileSelectionIsPreviewed =
                        previewedSelectedItems !== undefined && fileIsSelected !== fileIsDisplayedAsSelected;

                      return (
                        <SingleItemDownload
                          key={file.url}
                          file={file}
                          checkbox={
                            <Checkbox
                              edge="start"
                              checked={fileIsDisplayedAsSelected}
                              disabled={zipActive}
                              data-selection-preview={fileSelectionIsPreviewed || undefined}
                              sx={
                                fileSelectionIsPreviewed
                                  ? {
                                      bgcolor: 'action.hover',
                                      borderRadius: '50%',
                                      color: 'secondary.main',
                                      outline: '1px dashed',
                                      outlineColor: 'secondary.main',
                                      outlineOffset: '-2px',
                                      '&.Mui-checked': { color: 'secondary.main' },
                                    }
                                  : undefined
                              }
                              onMouseEnter={(event) => previewSelection(index, event.shiftKey)}
                              onMouseMove={(event) => previewSelection(index, event.shiftKey)}
                              onMouseLeave={clearSelectionPreview}
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleSelection(index, e.shiftKey);
                              }}
                            />
                          }
                          index={unsortedFileNames ? undefined : index}
                          disabled={zipActive}
                          setDidDownload={setDidDownload}
                        />
                      );
                    })}
                  </MenuList>
                </Grid>
              </>
            )}
          </Grid>
          {!singleFile && (
            <Grid item mt={2}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                You can also click on a file name to start downloading it individually.
              </Typography>
            </Grid>
          )}
        </DialogContent>
      ) : (
        <DialogContent dividers>
          <Typography variant="body1">{emptyMessage}</Typography>
        </DialogContent>
      )}
      <DialogChromeFooter sticky>
        <Button onClick={handleClose}>Close</Button>
      </DialogChromeFooter>
    </Dialog>
  );
};
